import React, { ReactNode, CSSProperties } from "react";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import styled from "styled-components";

export const KenButtonDefault = styled(Button)`
  border-radius: 3px;
  min-height: 36px;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  padding: 8px 12px;
  text-transform: none;
`;

const KenButtonSecondary = styled(KenButtonDefault)`
  border: 1px solid #b3bac5;
`;

const StatusButton = styled(Button)`
  border-radius: 4px;
  color: white;
  height: 20px;
  font-weight: 500;
  font-size: 12px;
  padding: 4px 8px;
  box-shadow: none;
  text-transform: none;
`;

const RoundedButton = styled(Button)`
  border-radius: 32px;
  background: linear-gradient(114.33deg, #20365c 6.4%, #060b6b 108.85%);
  color: white;
  height: 40px;
  font-weight: 500;
  font-size: 14px;
  padding: 9px 32px;
  box-shadow: none;
  text-transform: none;
  &&& {
    &.Mui-disabled {
      color: rgba(0, 0, 0, 0.26);
      background: #f3f5f7;
    }
  }
`;
const RoundedButtonOutlined = styled(Button)`
  border-radius: 32px;
  background: white;
  color: linear-gradient(114.33deg, #20365c 6.4%, #060b6b 108.85%);
  height: 40px;
  font-weight: 500;
  font-size: 14px;
  border: 2px solid linear-gradient(114.33deg, #20365c 6.4%, #060b6b 108.85%);
  padding: 9px 32px;
  box-shadow: none;
  text-transform: none;
  &&& {
    &.Mui-disabled {
      color: rgba(0, 0, 0, 0.26);
      background: #f3f5f7;
    }
  }
`;


interface KenButtonProps {
  variant?: "primary" | "secondary" | "roundedButton" | "roundedButtonOutlined" | "error" | "success";
  icon?: string;
  label?: string;
  children?: ReactNode;
  buttonClass?: string;
  disabled?: boolean;
  style?: CSSProperties;
  type?: "button" | "submit" | "reset";
  form?: string;
  startIcon?: ReactNode;
  muiVariant?: "text" | "contained" | "outlined";
  onClick?: () => void;
}

export default function KenButton(props: KenButtonProps) {
  const { variant, icon, label, children, buttonClass, type, form } = props;
  const muiVariantMap: Record<string, "text" | "contained" | "outlined"> = {
    primary: "contained",
    secondary: "outlined",
    roundedButton: "contained",
    roundedButtonOutlined: "outlined",
    error: "contained",
    success: "contained",
  };
  const {
    muiVariant = muiVariantMap[variant || "primary"],
    ...muiProps
  } = props;
  const propsCopy: KenButtonProps = Object.assign({}, props);
  if (icon) {
    propsCopy.startIcon = React.createElement('img', { src: icon, alt: 'icon' });
    propsCopy.muiVariant = muiVariantMap[variant || "primary"];
  }

  switch (variant) {
    case "primary":
      return (
        <KenButtonDefault
          data-testid="button"
          type={type}
          form={form}
          {...propsCopy}
          variant={muiVariantMap[variant || "primary"]} // Ensure variant matches Material-UI expected values
          disableElevation
          className={buttonClass}
        >
          {label || children}
        </KenButtonDefault>
      );
    case "secondary":
      return (
        <KenButtonSecondary data-testid="button" {...propsCopy} variant="outlined" color="primary" className={buttonClass} type={type} form={form}>
          {label || children}
        </KenButtonSecondary>
      );
    case "roundedButton":
      return (
        <RoundedButton data-testid="button" {...propsCopy} variant="contained" color="primary" className={buttonClass}>
          {label || children}
        </RoundedButton>
      );
    case "roundedButtonOutlined":
      return (
        <RoundedButtonOutlined
          data-testid="button"
          {...propsCopy}
          variant="outlined"
          // color="primary"
          className={buttonClass}
        >
          {label || children}
        </RoundedButtonOutlined>
      );
    case "error":
      return (
        <StatusButton data-testid="button" {...propsCopy} variant="contained" color="error" type={type} form={form}>
          <Typography noWrap>{label || children}</Typography>
        </StatusButton>
      );
    case "success":
      return (
        <StatusButton data-testid="button" {...propsCopy} variant="contained" color="success" type={type} form={form}>
          {label || children}
        </StatusButton>
      );

    default:
      return (
        <KenButtonDefault
        data-testid="button"
        type={type}
        form={form}
        {...muiProps}
        variant={muiVariant}
        disableElevation
        className={buttonClass}
      >
        {label || children}
      </KenButtonDefault>
      );
  }
}
